<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMenuStore } from '../stores/menuStore'
import { useFixtureStore } from '../stores/fixtureStore'
import { useLeagueTeamsStore } from '../stores/leagueTeamsStore'
import RosterDrawer from './RosterDrawer.vue'
import LeagueLink from './LeagueLink.vue'
import AddPlayerModal from './AddPlayerModal.vue'
import { getLeagueId, hasValidLeagueId } from '../utils/leagueUtils'
import { useCurrentUserPermissions } from '../composables/usePermissions'
import axios from '../axios'
import { useGlobalPermissionsStore } from '../stores/globalPermissions'
import LeagueListItem from './LeagueListItem.vue'
import SimpleToast from './SimpleToast.vue'
import * as XLSX from 'xlsx'

const fixtureStore = useFixtureStore()
const leagueTeamsStore = useLeagueTeamsStore()
const menuStore = useMenuStore()

// Initialize permissions
const {
  isAdmin,
  isTrader,
  canManageFixtures,
  canTrade,
  canAccessServer,
  canAccessTrading,
  hasPermission,
  hasAnyPermission,
  canAccessSecondaryMenu,
  userPermissions,
  loading: permissionsLoading
} = useCurrentUserPermissions()

// Add drawer-related properties and methods
const drawerVisible = ref(false)
const drawerTeam = ref(null)
const drawerLeagueId = ref(null)

const closeDrawer = () => {
  drawerVisible.value = false
  drawerTeam.value = null
  drawerLeagueId.value = null
} 

const searchTeam = ref('')
const searchRosters = ref('')
const route = useRoute()
const router = useRouter()

// Add state for new competition form
const showAddCompetitionForm = ref(false)
const newCompetitionName = ref('')
const isSubmittingCompetition = ref(false)

// Add state for player modal
const showAddPlayerModal = ref(false)

// Add state for delete confirmation modal
const showDeleteModal = ref(false)
const teamToDelete = ref(null)
const leagueIdToDelete = ref(null)

// Add state for edit team name modal
const showEditTeamModal = ref(false)
const teamToEdit = ref(null)
const leagueIdToEdit = ref(null)
const editTeamName = ref('')
const isSubmittingEditTeam = ref(false)

// Add state for new team form
const showAddTeamForm = ref(false)
const newTeamName = ref('')
const newTeamLeagueId = ref(null)
const isSubmittingTeam = ref(false)

// Add state for toast notifications
const showToast = ref(false)
const toastConfig = ref({
  type: 'success',
  title: '',
  message: ''
})

// Modify currentView to track which tab is active
const activeTab = ref('fixtures') // Default to fixtures tab

// Function to switch between tabs and load appropriate data
const switchTab = async (tab) => {
  activeTab.value = tab
  
  switch(tab) {
    case 'fixtures':
      // Load leagues for fixtures
      await getLeagues()
      break
    case 'rosters':
      // Load leagues for rosters (teams grouped by league)
      await getLeagues()
      break
    case 'players':
      // Load players data
      await getPlayers()
      break
  }
}

const addTeamToLeague = (data) => {
  // Ensure we're passing the correct team data structure
  console.log('Adding team to league:', data)
  const teamData = {
    team: {
      team_name: data.team.team_name || data.team.name,
      id: data.team.id
    }
  }
  leagueTeamsStore.addTeamToLeague({ team: teamData.team, leagueId: data.leagueId })
  searchTeam.value = '' // Clear the search box after adding
}

watch(searchTeam, async (newVal) => {
  if (newVal && newVal.length > 0) {
    const results = await leagueTeamsStore.searchTeams(newVal)
    // Find the currently expanded league
    const expandedLeagueId = Array.from(expandedLeagues.value)[0]
    if (expandedLeagueId) {
      leagueTeamsStore.teams[expandedLeagueId] = results
    }
  } else {
    // If search is cleared, reload the original teams for the expanded league
    const expandedLeagueId = Array.from(expandedLeagues.value)[0]
    if (expandedLeagueId) {
      await leagueTeamsStore.fetchTeamsByLeague(expandedLeagueId)
    }
  }
})

const currentView = computed(() => {
  // Check if the path starts with '/games/fixtures'
  if (route.path.startsWith('/games/fixtures')) {
    return 'fixtures'
  }
  // For other routes, use the last segment
  return route.path.split('/').pop()
})

// Props for different views
const props = defineProps({
  leagues: {
    type: Array,
    default: () => []
  },
  selectedLeague: {
    type: String,
    default: ''
  }
})

// Sample data for different views
const rosters = {
  'NBA': ['Team A', 'Team B', 'Team C'],
  'WNBA': ['Team X', 'Team Y', 'Team Z'],
}

const players = ref([])

// API functions for different data types
const getPlayers = async () => {
  try {
    const response = await axios.get('/api/players/get-players')
    players.value = response.data
  } catch (error) {
    console.error('Error fetching players:', error)
    players.value = []
  }
}

const getLeagues = async () => {
  try {
    const response = await axios.get('/api/leagues/get-leagues')
    leagues.value = response.data
  } catch (error) {
    console.error('Error fetching leagues:', error)
    leagues.value = []
  }
}

const getTeams = async () => {
  try {
    const response = await axios.get('/api/teams/get-teams')
    // This would be used if you want to show all teams without league grouping
    return response.data
  } catch (error) {
    console.error('Error fetching teams:', error)
    return []
  }
}

const selectedTeam = ref('Latvian Estonian Basketball League - BC Kalev/Cramo')
const leagues = ref([])
const searchLeagues = ref('')

// Computed property to filter leagues based on search
const filteredLeagues = computed(() => {
  if (!searchLeagues.value.trim()) {
    return leagues.value
  }
  
  const searchTerm = searchLeagues.value.toLowerCase().trim()
  return leagues.value.filter(league => {
    const leagueName = (league.league_name || league.name || '').toLowerCase()
    return leagueName.includes(searchTerm)
  })
})

// Computed property to filter leagues for rosters tab
const filteredRosterLeagues = computed(() => {
  if (!searchRosters.value.trim()) {
    return leagues.value
  }
  
  const searchTerm = searchRosters.value.toLowerCase().trim()
  return leagues.value.filter(league => {
    const leagueName = (league.league_name || league.name || '').toLowerCase()
    return leagueName.includes(searchTerm)
  })
})

watch(
  () => menuStore.secondaryMenuTab,
  (tab) => {
    if (tab && tab !== activeTab.value) {
      switchTab(tab)
    }
  },
  { immediate: true }
)

onMounted(async () => {
  // Load initial data based on current tab
  await switchTab(activeTab.value)
  document.addEventListener('click', handleExportDropdownClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleExportDropdownClickOutside)
})
// const leagues = [
//   { id: 'nba', name: 'NBA', count: 8 },
//   { id: 'wnba-playoffs', name: 'WNBA Play-Offs', count: 5 },
//   { id: 'nba-playoffs', name: 'NBA Playoffs', count: 3 },
//   { id: 'wnba', name: 'WNBA', count: 4 },
//   { id: 'latvian-estonian', name: 'Latvian-Estonian Basketball League', count: 6 },
//   { id: 'estonian-cup', name: 'Estonian Cup', count: 2 },
//   { id: 'lkl', name: 'Lithuania Basketball league (LKL)', count: 4 },
//   { id: 'fiba-qual', name: 'Fiba Champions League Qualification', count: 3 },
//   { id: 'fiba-europe', name: 'FIBA Europe Cup', count: 5 },
//   { id: 'eurocup', name: 'EuroCup', count: 7 },
//   { id: 'enbl', name: 'ENBL', count: 4 },
//   { id: 'euroleague', name: 'Euroleague', count: 8 },
//   { id: 'fiba-cl', name: 'FIBA Champions League', count: 6 },
//   { id: 'lithuanian-cup', name: 'Lithuanian Cup', count: 3 },
//   { id: 'fiba-qual', name: 'FIBA Eurobasket Qualifiers', count: 2 }
// ]

// Add ref to track if an operation is in progress
const isOperationInProgress = ref(false)

const handleFixtureHover = async (league) => {
  // Prevent rapid clicks
  if (isOperationInProgress.value) {
    return
  }
  
  isOperationInProgress.value = true
  
  try {
    const leagueId = getLeagueId(league)
    
    // Prevent action if no valid league ID
    if (!hasValidLeagueId(league)) {
      console.warn('League does not have a valid ID:', league)
      return
    }
    
    if (menuStore.showThirdMenu && leagueId?.toString() === route.params.id) {
      menuStore.closeThirdMenu()
    } else {
      menuStore.openThirdMenu(league)
      // Fetch fixtures for the selected league
      try {
        await fixtureStore.getFixturesByLeagueId(leagueId)
      } catch (error) {
        console.error('Error fetching fixtures for league:', leagueId, error)
      }
    }
  } finally {
    // Add a small delay to prevent rapid successive clicks
    setTimeout(() => {
      isOperationInProgress.value = false
    }, 300)
  }
}

// Add navigation functions
const navigateToView = (view) => {
  switch(view) {
    case 'fixtures':
      router.push('/games/fixtures')
      break
    case 'rosters':
      router.push('/games/rosters')
      break
    case 'players':
      router.push('/games/players')
      break
  }
}

// Add ref to track expanded leagues
const expandedLeagues = ref(new Set(['wnba'])) // WNBA starts expanded as shown in the image

watch(
  () => menuStore.showCreateTeamForm,
  (shouldShow) => {
    if (!shouldShow) return
    showAddTeamForm.value = true
    newTeamName.value = ''
    newTeamLeagueId.value = Array.from(expandedLeagues.value)[0] || null
    menuStore.showCreateTeamForm = false
  },
  { immediate: true }
)

// Function to toggle league expansion
const toggleLeague = async (leagueId) => {
  if (expandedLeagues.value.has(leagueId)) {
    expandedLeagues.value.delete(leagueId)
    leagueTeamsStore.clearTeams(leagueId)
  } else {
    // Clear any existing expanded leagues
    expandedLeagues.value.clear()
    // Add the new league
    expandedLeagues.value.add(leagueId)
    // Set the expanded league in the store
    leagueTeamsStore.setExpandedLeague(leagueId)
    // Fetch teams for the league from the API
    await leagueTeamsStore.fetchTeamsByLeague(leagueId)
  }
}

// Remove the old leagueTeams ref and replace with computed
const leagueTeams = computed(() => {
  const expandedLeagueId = Array.from(expandedLeagues.value)[0]
  return expandedLeagueId ? leagueTeamsStore.getTeamsByLeague(expandedLeagueId) : []
})

// Function to get league slug (simplified version for the example)
const getLeagueSlug = (leagueName) => {
  return leagueName.toLowerCase().replace(/\s+/g, '-')
}

const handlePlayerClick = (player) => {
  menuStore.openThirdMenu({
    ...player,
    type: 'player',
  }, 'player')
}

const handleTeamClick = (team, leagueId) => {
  const league = leagues.value.find(l => l.id === leagueId || l.league_id === leagueId)
  menuStore.openThirdMenu({
    team,
          league: league ? { league_name: league.league_name, league_id: getLeagueId(league) } : null
  }, 'roster')
}

// Add functions for competition management
const showAddCompetitionFormHandler = () => {
  showAddCompetitionForm.value = true
  newCompetitionName.value = ''
}

const cancelAddCompetition = () => {
  showAddCompetitionForm.value = false
  newCompetitionName.value = ''
}

const saveNewCompetition = async () => {
  if (!newCompetitionName.value.trim()) {
    return
  }

  isSubmittingCompetition.value = true

  try {
    // Send to Laravel API - adjust the endpoint as needed
    const response = await axios.post('/api/leagues/create-league', {
      league_name: newCompetitionName.value.trim()
    })

    // Add the new league to the local list
    if (response.data && response.data.league) {
      leagues.value.unshift(response.data.league)
    } else {
      // Fallback if response structure is different
      leagues.value.unshift({
        league_id: response.data.id || Date.now(),
        league_name: newCompetitionName.value.trim(),
        id: response.data.id || Date.now()
      })
    }

    // Reset form
    showAddCompetitionForm.value = false
    newCompetitionName.value = ''
    
    console.log('League created successfully:', response.data)
  } catch (error) {
    console.error('Error creating league:', error)
    // You might want to show a notification to the user here
    alert('Error creating competition. Please try again.')
  } finally {
    isSubmittingCompetition.value = false
  }
}

// Player modal functions
const showAddPlayerModalHandler = () => {
  showAddPlayerModal.value = true
}

const closeAddPlayerModal = () => {
  showAddPlayerModal.value = false
}

const handlePlayerAdded = (newPlayer) => {
  // Add the new player to the local players list
  players.value.unshift(newPlayer)
  console.log('Player added successfully:', newPlayer)
}

// Add delete team functionality
const showDeleteConfirmation = (team, leagueId) => {
  teamToDelete.value = team
  leagueIdToDelete.value = leagueId
  showDeleteModal.value = true
}

const confirmDeleteTeam = async () => {
  if (!teamToDelete.value) return

  // Use the league_team row id (pivot table id)
  const rowId = teamToDelete.value.id
  if (!rowId) {
    console.error('No row id found for team to delete')
    return
  }

  const teamName = teamToDelete.value.team_name || teamToDelete.value.team?.team_name

  try {
    // Call API to delete league_team row by id
    await axios.delete(`/api/league-teams/${rowId}`)
    
    // Remove team from local store using the row id
    const leagueId = leagueIdToDelete.value || teamToDelete.value.league_id
    if (leagueId) {
      leagueTeamsStore.removeTeamFromLeague(leagueId, rowId)
    }
    
    console.log('Team deleted successfully:', teamToDelete.value)
    
    // Close modal and reset state
    closeDeleteModal()
    
    // Show success notification
    showToast.value = true
    toastConfig.value = {
      type: 'success',
      title: 'Team Deleted Successfully',
      message: `"${teamName}" has been removed from the league`
    }
  } catch (error) {
    console.error('Error deleting team:', error)
    
    // Close modal first
    closeDeleteModal()
    
    // Show error notification
    showToast.value = true
    toastConfig.value = {
      type: 'error',
      title: 'Error Deleting Team',
      message: error.response?.data?.message || 'Failed to delete team. Please try again.'
    }
  }
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  teamToDelete.value = null
  leagueIdToDelete.value = null
}

const showEditTeamModalHandler = (team, leagueId) => {
  teamToEdit.value = team
  leagueIdToEdit.value = leagueId
  editTeamName.value = team.team_name || team.team?.team_name || ''
  showEditTeamModal.value = true
}

const closeEditTeamModal = () => {
  showEditTeamModal.value = false
  teamToEdit.value = null
  leagueIdToEdit.value = null
  editTeamName.value = ''
  isSubmittingEditTeam.value = false
}

const saveEditTeamName = async () => {
  if (!teamToEdit.value || !editTeamName.value.trim()) return

  const teamId = teamToEdit.value.team_id || teamToEdit.value.team?.id
  if (!teamId) {
    console.error('No team id found for team to edit')
    return
  }

  const newName = editTeamName.value.trim()
  isSubmittingEditTeam.value = true

  try {
    await axios.patch(`/api/teams/${teamId}/team-name`, {
      team_name: newName
    })

    const leagueId = leagueIdToEdit.value
    if (leagueId) {
      await leagueTeamsStore.fetchTeamsByLeague(leagueId)
    }

    closeEditTeamModal()

    showToast.value = true
    toastConfig.value = {
      type: 'success',
      title: 'Team Name Updated',
      message: `Team name has been updated to "${newName}"`
    }
  } catch (error) {
    console.error('Error updating team name:', error)

    showToast.value = true
    toastConfig.value = {
      type: 'error',
      title: 'Error Updating Team Name',
      message: error.response?.data?.message || 'Failed to update team name. Please try again.'
    }
  } finally {
    isSubmittingEditTeam.value = false
  }
}

// Add team management functions
const showAddTeamFormHandler = () => {
  showAddTeamForm.value = true
  newTeamName.value = ''
  newTeamLeagueId.value = Array.from(expandedLeagues.value)[0] || null
}

const cancelAddTeam = () => {
  showAddTeamForm.value = false
  newTeamName.value = ''
  newTeamLeagueId.value = null
}

const saveNewTeam = async () => {
  if (!newTeamName.value.trim()) {
    return
  }

  const expandedLeagueId = newTeamLeagueId.value || Array.from(expandedLeagues.value)[0]
  if (!expandedLeagueId) {
    showToast.value = true
    toastConfig.value = {
      type: 'error',
      title: 'Error',
      message: 'Please select a league first'
    }
    return
  }

  isSubmittingTeam.value = true

  try {
    // Send to Laravel API to create team
    const response = await axios.post('/api/teams/create-team', {
      team_name: newTeamName.value.trim(),
      league_id: expandedLeagueId
    })

    const teamName = newTeamName.value.trim()

    // Add the new team to the local store
    if (response.data && response.data.team) {
      leagueTeamsStore.addTeamToLeague({ 
        team: response.data.team, 
        leagueId: expandedLeagueId 
      })
    }

    // Reset form and search
    showAddTeamForm.value = false
    newTeamName.value = ''
    newTeamLeagueId.value = null
    searchTeam.value = ''
    
    // Show success notification
    showToast.value = true
    toastConfig.value = {
      type: 'success',
      title: 'Team Added Successfully',
      message: `"${teamName}" has been added to the league`
    }
    
    console.log('Team created successfully:', response.data)
  } catch (error) {
    console.error('Error creating team:', error)
    
    // Show error notification
    showToast.value = true
    toastConfig.value = {
      type: 'error',
      title: 'Error Creating Team',
      message: error.response?.data?.message || 'Failed to create team. Please try again.'
    }
  } finally {
    isSubmittingTeam.value = false
  }
}

// Close toast notification
const closeToast = () => {
  showToast.value = false
}

// Export dropdown state (per league)
const exportDropdownLeagueId = ref(null)

const closeExportDropdown = () => {
  exportDropdownLeagueId.value = null
}

// Export league and team names to JSON
const exportToJson = (league) => {
  const leagueId = getLeagueId(league)
  const teams = leagueTeamsStore.getTeamsByLeague(leagueId).slice().sort((a, b) => {
    const nameA = (a.team_name || a.team?.team_name || '').toLowerCase()
    const nameB = (b.team_name || b.team?.team_name || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })
  const data = {
    league: league.league_name || league.name || 'Unknown League',
    teams: teams.map(t => ({
      name: t.team_name || t.team?.team_name || ''
    }))
  }
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `league-teams-${(league.league_name || league.name || 'export').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.json`
  a.click()
  URL.revokeObjectURL(url)
  closeExportDropdown()
}

// Export league and team names to CSV
const exportToCsv = (league) => {
  const leagueId = getLeagueId(league)
  const leagueName = league.league_name || league.name || 'Unknown League'
  const teams = leagueTeamsStore.getTeamsByLeague(leagueId).slice().sort((a, b) => {
    const nameA = (a.team_name || a.team?.team_name || '').toLowerCase()
    const nameB = (b.team_name || b.team?.team_name || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })
  const header = 'League,Team Name\n'
  const rows = teams.map(t => {
    const name = (t.team_name || t.team?.team_name || '').replace(/"/g, '""')
    return `"${leagueName}","${name}"`
  }).join('\n')
  const csv = header + rows
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `league-teams-${(leagueName).replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`
  a.click()
  URL.revokeObjectURL(url)
  closeExportDropdown()
}

// Export league and team names to Excel
const exportToExcel = (league) => {
  const leagueId = getLeagueId(league)
  const leagueName = league.league_name || league.name || 'Unknown League'
  const teams = leagueTeamsStore.getTeamsByLeague(leagueId).slice().sort((a, b) => {
    const nameA = (a.team_name || a.team?.team_name || '').toLowerCase()
    const nameB = (b.team_name || b.team?.team_name || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })
  const sheetData = teams.map(t => ({
    League: leagueName,
    'Team Name': t.team_name || t.team?.team_name || ''
  }))
  const ws = XLSX.utils.json_to_sheet(sheetData)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'League Teams')
  XLSX.writeFile(wb, `league-teams-${(leagueName).replace(/[^a-z0-9]/gi, '-').toLowerCase()}.xlsx`)
  closeExportDropdown()
}

// Close export dropdown when clicking outside
const handleExportDropdownClickOutside = (e) => {
  if (exportDropdownLeagueId.value && !e.target.closest('.export-dropdown-container')) {
    closeExportDropdown()
  }
}
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Top Navigation Icons - Fixed -->
    <div class="flex-shrink-0">
      <div class="flex items-center justify-between mb-4 mt-2 border-b border-gray-200 pb-2">
        <!-- Show permission loading state -->
        <div v-if="permissionsLoading" class="w-full flex justify-center py-4">
          <i class="fas fa-spinner fa-spin text-gray-400"></i>
          <span class="ml-2 text-sm text-gray-500">Loading permissions...</span>
        </div>
        
        <!-- Show tabs based on permissions -->
        <div v-else class="flex w-full items-center justify-between space-x-4 px-2">
          <!-- Fixtures tab - visible for trading and admin users -->
          <div 
            v-if="hasAnyPermission(['fixtures.view', 'trading.basic', 'admin.access'])"
            @click="switchTab('fixtures')"
            class="flex flex-col items-center group cursor-pointer hover:bg-gray-100 p-2 rounded-lg transition-colors"
            :class="{ 'bg-gray-100': activeTab === 'fixtures' }"
          >
            <i class="fas fa-calendar-alt text-gray-500 text-2xl"
               :class="activeTab === 'fixtures' ? 'text-pink-500' : 'group-hover:text-pink-500'"
            ></i>
            <span class="text-sm text-gray-500"
                  :class="activeTab === 'fixtures' ? 'text-pink-500' : 'group-hover:text-pink-500'"
            >Comps</span>
          </div>
          
          <!-- Rosters tab - visible for trading, admin, and roster management -->
          <div 
            v-if="hasAnyPermission(['rosters.view', 'teams.view', 'trading.basic', 'admin.access'])"
            @click="switchTab('rosters')"
            class="flex flex-col items-center group cursor-pointer hover:bg-gray-100 p-2 rounded-lg transition-colors"
            :class="{ 'bg-gray-100': activeTab === 'rosters' }"
          >
            <div class="flex items-center space-x-2">
              <i class="fas fa-basketball-ball text-gray-500 text-2xl"
                 :class="activeTab === 'rosters' ? 'text-pink-500' : 'group-hover:text-pink-500'"
              ></i>
              <i class="fas fa-list text-gray-500 text-2xl"
                 :class="activeTab === 'rosters' ? 'text-pink-500' : 'group-hover:text-pink-500'"
              ></i>
            </div>
            <span class="text-sm text-gray-500"
                  :class="activeTab === 'rosters' ? 'text-pink-500' : 'group-hover:text-pink-500'"
            >Rosters</span>
          </div>
          
          <!-- Players tab - visible for player management and admin users -->
          <div 
            v-if="hasAnyPermission(['players.view', 'players.manage', 'trading.basic', 'admin.access'])"
            @click="switchTab('players')"
            class="flex flex-col items-center group cursor-pointer hover:bg-gray-100 p-2 rounded-lg transition-colors"
            :class="{ 'bg-gray-100': activeTab === 'players' }"
          >
            <i class="fas fa-user text-gray-500 text-2xl"
               :class="activeTab === 'players' ? 'text-pink-500' : 'group-hover:text-pink-500'"
            ></i>
            <span class="text-sm text-gray-500"
                  :class="activeTab === 'players' ? 'text-pink-500' : 'group-hover:text-pink-500'"
            >Players</span>
          </div>
          
          <!-- No access message -->
          <!-- <div v-if="!hasAnyPermission(['fixtures.view', 'rosters.view', 'players.view', 'trading.basic', 'admin.access'])" 
               class="w-full text-center py-4">
            <i class="fas fa-lock text-gray-400 text-2xl mb-2"></i>
            <p class="text-sm text-gray-500">No menu access permissions</p>
          </div> -->
        </div>
      </div>
    </div>

    <!-- Scrollable Content Area -->
    <div class="flex-grow overflow-hidden">
      <template v-if="activeTab === 'fixtures'">
        <!-- Search and Header Section -->
        <div class="space-y-3 mb-4 px-2">
          <div class="relative">
            <input 
              type="text" 
              v-model="searchLeagues"
              placeholder="Search leagues..."
              class="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
            >
            <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
          </div>
          
          <div class="flex items-center justify-between px-2">
            <h3 class="font-semibold text-gray-500">LEAGUES</h3>
            <button 
              v-if="hasAnyPermission(['fixtures.create', 'admin.access', 'leagues.manage'])"
              class="text-pink-500 text-sm hover:text-pink-600"
              @click="showAddCompetitionFormHandler"
              :disabled="showAddCompetitionForm"
            >
              + Add Competition
            </button>
          </div>
        </div>
        
        <!-- Add Competition Form -->
        <div v-if="showAddCompetitionForm" class="px-2 mb-4">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Competition Name
              </label>
              <input 
                v-model="newCompetitionName"
                type="text" 
                placeholder="Enter competition name..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                :disabled="isSubmittingCompetition"
                @keyup.enter="saveNewCompetition"
                @keyup.escape="cancelAddCompetition"
              >
            </div>
            <div class="flex space-x-2">
              <button 
                @click="saveNewCompetition"
                :disabled="!newCompetitionName.trim() || isSubmittingCompetition"
                class="flex-1 bg-pink-500 text-white px-1 py-1 rounded-md hover:bg-pink-600 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <i v-if="isSubmittingCompetition" class="fas fa-spinner fa-spin mr-2"></i>
                {{ isSubmittingCompetition ? 'Saving...' : 'Save Competition' }}
              </button>
              <button 
                @click="cancelAddCompetition"
                :disabled="isSubmittingCompetition"
                class="px-1 py-1 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <!-- Scrollable Leagues list -->
        <div class="overflow-y-auto h-[calc(100vh-200px)]">
          <ul class="space-y-0 px-2">
            <li v-for="league in filteredLeagues" :key="league.id || league.league_id">
              <LeagueLink 
                :league="league"
                :disabled="isOperationInProgress"
                @click="handleFixtureHover(league)"
              >
                <template #default="{ league }">
                  <LeagueListItem 
                    :league="league" 
                    :is-loading="isOperationInProgress"
                  />
                </template>
              </LeagueLink>
            </li>
          </ul>
        </div>
      </template>

      <template v-else-if="activeTab === 'rosters'">
        <!-- Search and Header Section -->
        <div class="space-y-3 mb-4 px-2">
          <div class="relative">
            <input 
              type="text" 
              v-model="searchRosters"
              placeholder="Search rosters..."
              class="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
            >
            <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
          </div>

          <div class="flex items-center justify-between px-2">
            <h3 class="font-semibold text-gray-500">ROSTERS</h3>
            <button 
              v-if="hasAnyPermission(['rosters.create', 'teams.manage', 'admin.access'])"
              class="text-pink-500 text-sm hover:text-pink-600"
              @click="showAddTeamFormHandler"
            >
              + Add Roster
            </button>
          </div>

          <div v-if="showAddTeamForm" class="px-2">
            <div class="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">League</label>
                <select
                  v-model="newTeamLeagueId"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  :disabled="isSubmittingTeam"
                >
                  <option :value="null" disabled>Select a league</option>
                  <option
                    v-for="league in leagues"
                    :key="getLeagueId(league)"
                    :value="getLeagueId(league)"
                  >
                    {{ league.league_name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Team Name</label>
                <input
                  v-model="newTeamName"
                  type="text"
                  placeholder="Enter team name..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  :disabled="isSubmittingTeam"
                  @keyup.enter="saveNewTeam"
                  @keyup.escape="cancelAddTeam"
                >
              </div>
              <div class="flex space-x-2">
                <button
                  @click="saveNewTeam"
                  :disabled="!newTeamName.trim() || !newTeamLeagueId || isSubmittingTeam"
                  class="flex-1 bg-pink-500 text-white px-3 py-2 rounded-md hover:bg-pink-600 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {{ isSubmittingTeam ? 'Creating...' : 'Create Team' }}
                </button>
                <button
                  @click="cancelAddTeam"
                  :disabled="isSubmittingTeam"
                  class="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          
        </div>

        <!-- Scrollable League Sections -->
        <div class="overflow-y-auto h-[calc(100vh-200px)] px-2">
          <div class="space-y-2 bg-gray-50 -mx-4 px-4 py-2">
                          <div v-for="league in filteredRosterLeagues" 
                 :key="getLeagueId(league)" 
                 :class="[
                   'bg-white rounded-xl shadow-sm overflow-hidden border',
                   expandedLeagues.has(getLeagueId(league)) ? 'border-pink-400' : 'border-gray-100'
                 ]"
            >
              <div 
                class="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                @click="toggleLeague(getLeagueId(league))"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-basketball-ball text-gray-400"></i>
                  <span class="font-medium text-gray-700">{{ league.league_name }}</span>
                </div>
                <div class="flex items-center space-x-3">
                  <span 
                    v-if="leagueTeamsStore.getTeamsCount(getLeagueId(league)) > 0"
                    class="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600"
                  >
                    <!-- {{ league.count }} -->
                  </span>
                  <i 
                    class="fas text-gray-400 transition-transform duration-200"
                    :class="[
                      expandedLeagues.has(getLeagueId(league)) ? 'fa-chevron-up rotate-180' : 'fa-chevron-down',
                    ]"
                  ></i>
                </div>
              </div>

              <!-- Expanded Content with updated styling -->
              <template v-if="expandedLeagues.has(getLeagueId(league))">
                <div class="px-4 pt-3 pb-1 text-xs text-gray-500 font-medium flex items-center justify-between gap-2">
                  <span>{{ leagueTeamsStore.getTeamsCount(getLeagueId(league)) }} team{{ leagueTeamsStore.getTeamsCount(getLeagueId(league)) === 1 ? '' : 's' }}</span>
                  <div class="flex items-center gap-2">
                    <span v-if="leagueTeamsStore.loading" class="text-gray-400">Loading...</span>
                    <div v-if="leagueTeamsStore.getTeamsCount(getLeagueId(league)) > 0" class="relative export-dropdown-container">
                      <button 
                        @click.stop="exportDropdownLeagueId = exportDropdownLeagueId === getLeagueId(league) ? null : getLeagueId(league)"
                        class="p-1.5 text-gray-400 hover:text-pink-500 hover:bg-pink-50 rounded transition-colors"
                        title="Export league and teams"
                      >
                        <i class="fas fa-download text-sm"></i>
                      </button>
                      <div 
                        v-if="exportDropdownLeagueId === getLeagueId(league)"
                        @click.stop
                        class="absolute right-0 top-full mt-1 py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[120px]"
                      >
                        <button 
                          @click.stop="exportToJson(league)"
                          class="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <i class="fas fa-file-code text-amber-600"></i> JSON
                        </button>
                        <button 
                          @click.stop="exportToCsv(league)"
                          class="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <i class="fas fa-file-csv text-green-600"></i> CSV
                        </button>
                        <button 
                          @click.stop="exportToExcel(league)"
                          class="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <i class="fas fa-file-excel text-emerald-600"></i> Excel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="px-4 py-3 border-t border-gray-100 bg-gray-50">
                  <div class="relative">
                    <input 
                      type="text" 
                      v-model="searchTeam"
                      placeholder="Search teams to add"
                      class="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 bg-white"
                    >
                    <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
                  </div>
                </div>
              
                <!-- Teams List -->
                <div class="border-t border-gray-100">
                  <!-- Regular Teams List (shown when no search) -->
                  
                  <template v-if="!searchTeam">
                    <div 
                      v-for="team in leagueTeamsStore.getTeamsByLeague(getLeagueId(league)).slice().sort((a, b) => {
                        const nameA = (a.team_name || a.team?.team_name || '').toLowerCase();
                        const nameB = (b.team_name || b.team?.team_name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      })"
                      :key="team.id" 
                      :class="[
                        team.team_id === menuStore.thirdMenuContent?.team?.team_id
                          ? 'bg-pink-50 text-pink-500'
                          : 'text-gray-700 hover:bg-gray-100'
                      ]"
                      class="border-b border-gray-100 last:border-b-0"
                    >
                      <div 
                        class="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between group"
                        @click="handleTeamClick(team, getLeagueId(league))"
                      >
                        <div class="flex items-center space-x-3">
                          <i class="fas fa-shield-alt text-gray-400 group-hover:text-pink-500"></i>
                          <span class="text-gray-700 group-hover:text-gray-900">{{ team.team_name || team.team?.team_name }} ({{ team.team_id }})</span>
                        </div>
                        <div class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            v-if="hasAnyPermission(['teams.manage', 'rosters.manage', 'admin.access'])"
                            @click.stop="showEditTeamModalHandler(team, getLeagueId(league))"
                            class="text-gray-400 hover:text-pink-600 p-1 rounded hover:bg-pink-50"
                            title="Edit team name"
                          >
                            <i class="fas fa-pencil-alt text-sm"></i>
                          </button>
                          <button 
                            v-if="hasAnyPermission(['teams.delete', 'rosters.manage', 'admin.access'])"
                            @click.stop="showDeleteConfirmation(team, getLeagueId(league))" 
                            class="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                            title="Delete team"
                          >
                            <i class="fas fa-trash text-sm"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Search Results (shown when searching) -->
                  <template v-else-if="searchTeam && leagueTeams.length > 0">
                    <div 
                      v-for="team in leagueTeams" 
                      :key="team.id" 
                      class="border-b border-gray-100 last:border-b-0"
                    >
                      <div 
                        class="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between group"
                        @click="handleTeamClick(team, getLeagueId(league))"
                      >
                        <div class="flex items-center space-x-3">
                          <i class="fas fa-shield-alt text-gray-400 group-hover:text-pink-500"></i>
                          <span class="text-gray-700 group-hover:text-gray-900">{{ team.team_name || team.team?.team_name }} ({{ team.team_id }})</span>
                        </div>
                        <button 
                          v-if="hasAnyPermission(['teams.add', 'rosters.manage', 'admin.access'])"
                          @click.stop="addTeamToLeague({team, leagueId: getLeagueId(league)})" 
                          class="text-pink-500 hover:text-pink-600 font-semibold"
                        >
                          Add +
                        </button>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="searchTeam && leagueTeams.length === 0">
                    <!-- No teams found - Add Team Form -->
                    <div v-if="!showAddTeamForm" class="px-4 py-4 text-center space-y-3">
                      <div class="text-gray-500 text-sm">
                        No teams found
                      </div>
                      <button 
                        v-if="hasAnyPermission(['teams.create', 'teams.manage', 'admin.access'])"
                        @click="showAddTeamFormHandler"
                        class="text-pink-500 hover:text-pink-600 text-sm font-medium flex items-center justify-center mx-auto"
                      >
                        <i class="fas fa-plus-circle mr-2"></i>
                        Add "{{ searchTeam }}" as new team
                      </button>
                    </div>
                    
                    <!-- Add Team Form -->
                    <div v-else class="px-4 py-4">
                      <div class="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-2">
                            Team Name
                          </label>
                          <input 
                            v-model="newTeamName"
                            type="text" 
                            :placeholder="searchTeam"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            :disabled="isSubmittingTeam"
                            @keyup.enter="saveNewTeam"
                            @keyup.escape="cancelAddTeam"
                          >
                        </div>
                        <div class="flex space-x-2">
                          <button 
                            @click="saveNewTeam"
                            :disabled="!newTeamName.trim() || isSubmittingTeam"
                            class="flex-1 bg-pink-500 text-white px-3 py-2 rounded-md hover:bg-pink-600 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-sm"
                          >
                            <i v-if="isSubmittingTeam" class="fas fa-spinner fa-spin mr-2"></i>
                            {{ isSubmittingTeam ? 'Creating...' : 'Create Team' }}
                          </button>
                          <button 
                            @click="cancelAddTeam"
                            :disabled="isSubmittingTeam"
                            class="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="activeTab === 'players'">
        <!-- Search and Header Section -->
        <div class="space-y-3 mb-4 px-2">
          <div class="relative">
            <input 
              type="text" 
              placeholder="Search players..."
              class="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
            >
            <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
          </div>

          <div class="flex items-center justify-between">
            <h3 class="font-semibold text-gray-500">PLAYERS</h3>
            <button 
              v-if="hasAnyPermission(['players.create', 'players.manage', 'admin.access', 'trading.basic'])"
              class="text-pink-500 text-sm hover:text-pink-600"
              @click="showAddPlayerModalHandler"
            >
              + Add Player
            </button>
          </div>
        </div>

        <!-- Scrollable Players list -->
        <div class="overflow-y-auto h-[calc(100vh-200px)]">
          <div class="space-y-1 flex flex-col pr-1 px-2">
            <div class="flex items-center justify-between text-sm text-gray-500 px-2">
              <span>{{ players.length }} players</span>
            </div>
            <div v-for="player in players" 
                 :key="player"
                 class="p-2 hover:bg-gray-50 rounded-lg cursor-pointer flex items-center justify-between"
                 @click="handlePlayerClick(player)"
            >
              <span>{{ player.player_name }}</span>
              <i class="fas fa-chevron-right text-gray-400"></i>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <RosterDrawer
    :visible="drawerVisible"
    :team="drawerTeam"
    :leagueId="drawerLeagueId"
    @close="closeDrawer"
  />

  <!-- Add Player Modal -->
  <AddPlayerModal
    :visible="showAddPlayerModal"
    @close="closeAddPlayerModal"
    @player-added="handlePlayerAdded"
  />

  <!-- Edit Team Name Modal — teleported + centered panel over dimmed backdrop -->
  <Teleport to="body">
    <div
      v-if="showEditTeamModal"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-team-name-title"
      @click="closeEditTeamModal"
    >
      <div
        class="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
        @click.stop
      >
        <header class="border-b border-gray-200 px-5 py-4">
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
              <i class="fas fa-pencil-alt text-pink-600"></i>
            </div>
            <div class="flex-1 min-w-0">
              <h3 id="edit-team-name-title" class="text-lg font-semibold text-gray-900">
                Edit team name
              </h3>
              <p class="text-sm text-gray-500 mt-0.5">
                Team ID: {{ teamToEdit?.team_id || teamToEdit?.team?.id }}
              </p>
            </div>
            <button
              type="button"
              @click="closeEditTeamModal"
              :disabled="isSubmittingEditTeam"
              class="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </header>

        <div class="px-5 py-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Team Name
          </label>
          <input
            v-model="editTeamName"
            type="text"
            class="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            :disabled="isSubmittingEditTeam"
            autofocus
            @keyup.enter="saveEditTeamName"
            @keyup.escape="closeEditTeamModal"
          >
        </div>

        <footer class="flex gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
          <button
            type="button"
            @click="closeEditTeamModal"
            :disabled="isSubmittingEditTeam"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="saveEditTeamName"
            :disabled="!editTeamName.trim() || isSubmittingEditTeam"
            class="flex-1 px-4 py-2 border border-transparent rounded-lg text-white bg-pink-500 hover:bg-pink-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center text-sm font-medium"
          >
            <i v-if="isSubmittingEditTeam" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isSubmittingEditTeam ? 'Saving...' : 'Save' }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>

  <!-- Delete Confirmation Modal -->
  <div 
    v-if="showDeleteModal" 
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click="closeDeleteModal"
  >
    <div 
      class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
      @click.stop
    >
      <div class="flex items-center mb-4">
        <div class="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
          <i class="fas fa-exclamation-triangle text-red-600"></i>
        </div>
      </div>
      
      <div class="text-center">
        <h3 class="text-lg font-medium text-gray-900 mb-2">
          Delete Team
        </h3>
        <p class="text-sm text-gray-500 mb-6">
          Are you sure you want to delete 
          <span class="font-semibold text-gray-900">
            "{{ teamToDelete?.team_name || teamToDelete?.team?.team_name }}"
          </span>?
          <br>
          <span class="text-red-600">This action cannot be undone.</span>
        </p>
        
        <div class="flex space-x-3">
          <button
            @click="closeDeleteModal"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="confirmDeleteTeam"
            class="flex-1 px-4 py-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
          >
            Delete Team
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Toast Notification -->
  <SimpleToast
    :isVisible="showToast"
    :type="toastConfig.type"
    :title="toastConfig.title"
    :message="toastConfig.message"
    @close="closeToast"
  />
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Optional: Add smooth rotation animation for chevron */
.rotate-180 {
  transform: rotate(180deg);
}
</style>
}```` 